import { useState } from 'react';
import { Check, FileText, RefreshCw } from 'lucide-react';

import { mostrarAviso } from '@/modulos/operacao/comum/cadastroIndisponivel';
import { formatarData } from '@/nucleo/formato/datas';
import { STATUS_TONE } from '@/nucleo/statusBadgeTokens';
import { cn } from '@/nucleo/utilitarios';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/base/dialog';

import { AVISO_DE_ENVIO_INDISPONIVEL } from './avisos';
import type { DocumentoReutilizavel } from './contrato';

interface ReutilizarDocumentoModalProps {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
  tipoDocumento: string;
  /** Documentos aprovados antes, já filtrados pelo tipo. */
  documentos: DocumentoReutilizavel[];
}

/**
 * Escolha de um documento aprovado antes para atender o mesmo requisito — o
 * `ReutilizarDocumentoModal` do protótipo. Escolher é local; confirmar
 * mostra o aviso de indisponibilidade e não vincula nada, porque não há onde
 * gravar o vínculo (ver `DocumentChecklist`).
 */
export function ReutilizarDocumentoModal({
  open,
  onOpenChange,
  tipoDocumento,
  documentos,
}: ReutilizarDocumentoModalProps) {
  const [selecionadoId, setSelecionadoId] = useState<string | null>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="text-primary h-5 w-5" aria-hidden="true" />
            Reutilizar Documento
          </DialogTitle>
          <DialogDescription>
            Selecione um documento aprovado anteriormente para vincular a esta solicitação.
          </DialogDescription>
        </DialogHeader>

        {documentos.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center text-sm">
            Nenhum documento aprovado encontrado para &quot;{tipoDocumento}&quot;.
          </p>
        ) : (
          <div
            className="max-h-64 space-y-2 overflow-y-auto"
            role="radiogroup"
            aria-label="Documentos aprovados"
          >
            {documentos.map((documento) => {
              const selecionado = selecionadoId === documento.id;
              return (
                <button
                  key={documento.id}
                  type="button"
                  role="radio"
                  aria-checked={selecionado}
                  onClick={() => setSelecionadoId(documento.id)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                    selecionado ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
                  )}
                >
                  <div className="bg-muted flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                    {selecionado ? (
                      <Check className="text-primary h-4 w-4" aria-hidden="true" />
                    ) : (
                      <FileText className="text-muted-foreground h-4 w-4" aria-hidden="true" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{documento.arquivoNome}</p>
                    <p className="text-muted-foreground text-xs">
                      Enviado em {formatarData(documento.enviadoEm)}
                      {documento.validadeAte &&
                        ` • Válido até ${formatarData(documento.validadeAte)}`}
                    </p>
                  </div>
                  <Badge variant="outline" className={cn(STATUS_TONE.success, 'text-[10px]')}>
                    Aprovado
                  </Badge>
                </button>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            disabled={!selecionadoId}
            onClick={() => mostrarAviso(AVISO_DE_ENVIO_INDISPONIVEL)}
          >
            Reutilizar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
